import React, { ComponentType, memo, useMemo } from 'react'
import { useReactiveVar } from '@apollo/client'
import {
    ElementType,
    workspaceSelectedElement,
} from 'api/graphQL/reactiveVars/element'
import { useTreeViewStyles } from '../../styles/useTreeViewStyles'

import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { makeStyles, Theme, Typography } from '@material-ui/core'
import { DragIndicator } from '@material-ui/icons'
import clsx from 'clsx'
import { useTreeHandlers } from 'components/WorkspaceEditor/hooks'
import { useEditingPermissions } from 'components/WorkspaceEditor/hooks/useEditingPermissions'
import { useReorderItems } from 'components/WorkspaceEditor/hooks/useReorderItems'
import { useShallowHoveredItems } from 'components/WorkspaceEditor/HoveredItemStore'
import { DataNode } from 'components/WorkspaceEditor/types'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { useActivationDeactivationController } from '../NodeContents/useActivationDeactivationController'
import { withLazyLoad } from '../withLazyComponent'
import { AddIconProps } from './AddIcon'
import { useIsNodeBeingAdded } from './hooks/useIsNodeBeingAdded'
import { SwitcherIconProps } from './SwitcherIcon'
import { VisibilityIconProps } from './VisibilityIcon'

/** Props for rendering visibility icon of <TreeView /> component */
export interface TreeViewHoveredIconsProps {
    /** The node to render */
    node: DataNode
    /** Classes to style the components */
    classes: ReturnType<typeof useTreeViewStyles>
    /** Optional tree reference for virtualization support */
    treeRef?: React.MutableRefObject<{
        scrollTo?: (options: {
            key: string | number
            align?: 'top' | 'bottom' | 'auto'
            offset?: number
        }) => void
    }>
}

/**
 * Renders the visibility icon property of <TreeView /> component
 * @param {VisibilityIconProps} props - Props for rendering visibility icon
 * @returns {ReactElement} Node visibility icon
 */
const LazyAddIcon = withLazyLoad<AddIconProps>(() =>
    import('./AddIcon').then((module) => ({
        default: module.AddIcon as ComponentType<any>,
    }))
)

const LazyVisibilityIcon = withLazyLoad<VisibilityIconProps>(() =>
    import('./VisibilityIcon').then((module) => ({
        default: module.VisibilityIcon as ComponentType<any>,
    }))
)

const LazySwitcherIcon = withLazyLoad<SwitcherIconProps>(() =>
    import('./SwitcherIcon').then((module) => ({
        default: module.SwitcherIcon as ComponentType<any>,
    }))
)

interface styleProps {
    iconCount: number
}

const useStyles = makeStyles((theme: Theme) => ({
    hoveredIconWrapper: {
        position: 'absolute',
        left: ({ iconCount }: styleProps) => -theme.spacing(iconCount * 3),
        width: 'auto',
        alignItems: 'center',
    },
}))

export const TreeViewHoveredIcons = memo<TreeViewHoveredIconsProps>(
    ({ node, classes, treeRef }) => {
        const {
            isContentTeamFromStudioPage,
            articlesLoading,
            expandedKeys,
            setIsEditingPlainText,
        } = useShallowSelector((state) => ({
            isContentTeamFromStudioPage: state.isContentTeamFromStudioPage,
            articlesLoading: state.articlesLoading,
            expandedKeys: state.expandedKeys,
            setIsEditingPlainText: state.actions.setIsEditingPlainText,
        }))

        const {
            disableProductTypeInactivation,
        } = useActivationDeactivationController()

        const { isDragging } = useReorderItems()
        const { canEdit } = useEditingPermissions()
        // Use the extracted hook to check if the node is being added

        const isNodeBeingAdded = useIsNodeBeingAdded(node)

        const { previewFeatures } = usePreviewFeatures()
        const disablePTAutomation = !previewFeatures.hasPTAutomation

        const {
            isMainMenuDisplayed,
            selectedMainMenuNode,
            hoveredNode,
        } = useShallowHoveredItems((state) => ({
            isMainMenuDisplayed: state.isMainMenuDisplayed,
            selectedMainMenuNode: state.selectedMainMenuNode,
            hoveredNode: state.hoveredNode,
        }))
        const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
        const showMainMenuIcons = useMemo(
            () => node?.key === selectedMainMenuNode?.id,
            [node, selectedMainMenuNode]
        )
        const isIconVisible = useMemo(
            () =>
                isMainMenuDisplayed
                    ? showMainMenuIcons
                    : node?.key === (hoveredNode as DataNode)?.key,
            [node, hoveredNode, showMainMenuIcons, isMainMenuDisplayed]
        )

        const { selectedElementNode } = useReactiveVar(workspaceSelectedElement)
        const isVisibilityIconVisible = useMemo(
            () =>
                !isContentTeamFromStudioPage ||
                (isCurrentlyOnOMWorkspace &&
                    selectedElementNode?.elementType ===
                        ElementType.PRODUCTFAMILY) ||
                (isCurrentlyOnOMWorkspace &&
                    selectedElementNode?.elementType ===
                        ElementType.PRODUCTTYPE &&
                    !node.isProductType &&
                    !node.isProductGroup &&
                    !(node.contentItemView?.isForProductType === true)),
            [
                isContentTeamFromStudioPage,
                isCurrentlyOnOMWorkspace,
                selectedElementNode?.elementType,
                node.isProductType,
                node.isProductGroup,
                node.contentItemView?.isForProductType,
            ]
        )

        const isLoading = useMemo(() => articlesLoading.includes(node.key), [
            articlesLoading,
            node.key,
        ])

        const DragTooltipContent = () => (
            <>
                <Typography className={classes.tooltipTitle}>
                    Click to open
                </Typography>
                <Typography className={classes.tooltipSubtitle}>
                    (Hold to drag)
                </Typography>
            </>
        )

        const {
            handleDragIconClick,
            handleMouseHover,
            handleMouseLeave,
            handleShowHideProperty,
        } = useTreeHandlers()

        const isDragDisabled = !canEdit || isNodeBeingAdded

        const handleDragClick = (e: React.MouseEvent) => {
            if (!isDragDisabled) {
                handleDragIconClick(e, node)
            }
        }

        const handleDragMouseDown = !isDragDisabled
            ? () => setIsEditingPlainText(false)
            : undefined

        const dragIcon = (
            <DragIndicator
                fontSize="inherit"
                aria-label={
                    !isDragDisabled
                        ? 'Drag to reorder'
                        : isNodeBeingAdded
                        ? 'Drag disabled - content being added'
                        : 'Editing disabled - not in project'
                }
                role="button"
                onClick={handleDragClick}
                onMouseDown={handleDragMouseDown}
                data-testid="drag-indicator"
                data-disabled={isDragDisabled}
                className={clsx(
                    classes.dragIndicator,
                    !isDragDisabled ? classes.enabledIcon : classes.disabledIcon
                )}
            />
        )

        const isArticle = useMemo(() => node.type === NODE_TYPES.Article, [
            node,
        ])
        const iconContainerClass = useMemo(
            () =>
                isArticle
                    ? clsx(classes.iconContainer, classes.iconContainerArticle)
                    : classes.iconContainer,
            [isArticle, classes]
        )
        const { hoveredIconWrapper } = useStyles({
            iconCount:
                (isArticle ? 4 : 3) -
                (!isVisibilityIconVisible ? 1 : 0),
        })
        return (
            <GridContainer
                onMouseOver={() => handleMouseHover(node)}
                onMouseLeave={handleMouseLeave}
                className={hoveredIconWrapper}
                direction="row"
            >
                <GridItem
                    className={clsx(
                        iconContainerClass,
                        !isIconVisible && classes.hiddenHoveredIcon
                    )}
                >
                    {isIconVisible && (
                        <LazyAddIcon
                            node={node as DataNode}
                            isLoading={isLoading}
                        />
                    )}
                </GridItem>
                {isVisibilityIconVisible && (
                    <GridItem
                        className={clsx(
                            iconContainerClass,
                            !isIconVisible && classes.hiddenHoveredIcon
                        )}
                    >
                        {isIconVisible && (
                            <LazyVisibilityIcon
                                contentTeamFromStudioPage={
                                    isContentTeamFromStudioPage &&
                                    !isCurrentlyOnOMWorkspace
                                }
                                isLoading={isLoading}
                                onClickIcon={(isHidden: boolean) =>
                                    handleShowHideProperty(
                                        isHidden,
                                        node,
                                        disablePTAutomation
                                    )
                                }
                                disabled={
                                    (disableProductTypeInactivation(node) ??
                                        false) ||
                                    isNodeBeingAdded
                                }
                                isHidden={node.isHidden ?? false}
                                isArticle
                            />
                        )}
                    </GridItem>
                )}
                <GridItem
                    className={
                        !isIconVisible || isLoading
                            ? classes.hiddenHoveredIcon
                            : iconContainerClass
                    }
                >
                    {!isDragging && isIconVisible && (
                        <HoverTooltip
                            text={<DragTooltipContent />}
                            placements={['top', 'bottom']}
                        >
                            {dragIcon}
                        </HoverTooltip>
                    )}
                    {isDragging && dragIcon}
                </GridItem>
                {isArticle && (
                    <LazySwitcherIcon
                        type={node.type}
                        isLoading={isLoading}
                        isExpanded={expandedKeys?.includes(node.key)}
                        isDisabled={isNodeBeingAdded}
                        node={node}
                        treeRef={treeRef}
                    />
                )}
            </GridContainer>
        )
    }
)
